import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, GraduationCap, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
    { to: "/programs", label: "Programs" },
    { to: "/learning-paths", label: "Learning Paths" },
    { to: "/for-schools", label: "For Schools" },
    { to: "/corporate", label: "Corporate" },
    { to: "/community", label: "Community" },
    { to: "/resources", label: "Resources" },
    { to: "/verify", label: "Verify" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`sticky top-0 z-50 w-full border-b transition-colors ${
                scrolled ? "bg-background/85 backdrop-blur-xl border-border" : "bg-background/70 backdrop-blur-md border-transparent"
            }`}
        >
            <nav className="container-page flex h-16 items-center justify-between gap-4">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
                        <GraduationCap className="h-5 w-5" />
                    </span>
                    <span className="font-heading text-lg font-extrabold tracking-tight">
                        LARUTA<span className="text-primary">.</span>
                    </span>
                </Link>

                <div className="hidden lg:flex items-center gap-1">
                    {LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                            className="px-3 py-2 text-sm font-medium text-foreground/70 hover:text-primary rounded-md transition-colors"
                        >
                            {l.label}
                        </Link>
                    ))}
                </div>

                <div className="hidden lg:flex items-center gap-2">
                    {user ? (
                        <Button data-testid="nav-dashboard-btn" onClick={() => navigate("/dashboard")} className="rounded-full">
                            Dashboard <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    ) : (
                        <>
                            <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}>
                                Log in
                            </Button>
                            <Button data-testid="nav-register-btn" onClick={() => navigate("/register")} className="rounded-full">
                                Get Started
                            </Button>
                        </>
                    )}
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden p-2"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>

            {open && (
                <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
                    <div className="container-page py-4 flex flex-col gap-1">
                        {LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-muted"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <div className="flex gap-2 pt-3">
                            {user ? (
                                <Button className="flex-1 rounded-full" onClick={() => navigate("/dashboard")}>Dashboard</Button>
                            ) : (
                                <>
                                    <Button variant="outline" className="flex-1" onClick={() => navigate("/login")}>Log in</Button>
                                    <Button className="flex-1 rounded-full" onClick={() => navigate("/register")}>Get Started</Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
